import axios from 'axios';
import { API_BASE } from './config';
import type {
  RPCListResponse,
  RPCSingleResponse,
  RPCFormData,
  RPCTestResponse,
  RPCQueryParams
} from '../types/rpc';

/**
 * Fetch dashboard data for RPC monitoring
 * @returns Dashboard data including system health, networks, and recommendations
 */
export const fetchDashboardData = async () => {
  const response = await axios.get(`${API_BASE}/rpc-monitor/dashboard`, {
    headers: {
      'Cache-Control': 'no-cache'
    }
  });
  return response.data;
};

/**
 * Test an RPC endpoint
 * @param url RPC URL to test
 * @param network Network name
 * @returns Test result
 */
export const testRPCEndpoint = async (url: string, network: string) => {
  const response = await axios.post(`${API_BASE}/rpc-monitor/test`, {
    url,
    network
  });
  return response.data;
};

/**
 * Recover an RPC endpoint
 * @param id RPC endpoint ID
 * @returns Recovery result
 */
export const recoverRPCEndpoint = async (id: string) => {
  const response = await axios.post(`${API_BASE}/rpc/recover/${id}`);
  return response.data;
};

/**
 * Get RPC recovery events
 * @param network Optional network filter
 * @param limit Maximum number of events to return
 * @returns Recovery events
 */
export const getRPCRecoveryEvents = async (network?: string, limit: number = 50) => {
  const params = new URLSearchParams();
  if (network) params.append('network', network);
  params.append('limit', limit.toString());

  const response = await axios.get(`${API_BASE}/rpc/recovery-events?${params.toString()}`);
  return response.data;
};

/**
 * Configure RPC alerts
 * @param settings Alert settings
 * @returns Configuration result
 */
export const configureRPCAlerts = async (settings: any) => {
  const response = await axios.post(`${API_BASE}/rpc/alerts`, settings);
  return response.data;
};

/**
 * Configure RPC auto-recovery
 * @param settings Recovery settings
 * @returns Configuration result
 */
export const configureRPCRecovery = async (settings: any) => {
  const response = await axios.post(`${API_BASE}/rpc/recovery`, settings);
  return response.data;
};

/**
 * Get all RPC endpoints with filtering and pagination
 * @param params Query parameters
 * @returns List of RPC endpoints with pagination
 */
export const getAllRPCs = async (params?: RPCQueryParams): Promise<RPCListResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.network) queryParams.append('network', params.network);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.activeNetworksOnly) queryParams.append('activeNetworksOnly', 'true');
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());

  const response = await axios.get(`${API_BASE}/rpc-monitor/list?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get RPC endpoint by ID
 * @param id RPC endpoint ID
 * @returns RPC endpoint details
 */
export const getRPCById = async (id: string): Promise<RPCSingleResponse> => {
  const response = await axios.get(`${API_BASE}/rpc-monitor/${id}`);
  return response.data;
};

/**
 * Create new RPC endpoint
 * @param data RPC endpoint data
 * @returns Created RPC endpoint
 */
export const createRPC = async (data: RPCFormData): Promise<RPCSingleResponse> => {
  const response = await axios.post(`${API_BASE}/rpc-monitor/create`, data);
  return response.data;
};

/**
 * Update RPC endpoint
 * @param id RPC endpoint ID
 * @param data Updated RPC endpoint data
 * @returns Updated RPC endpoint
 */
export const updateRPC = async (id: string, data: Partial<RPCFormData>): Promise<RPCSingleResponse> => {
  const response = await axios.put(`${API_BASE}/rpc-monitor/update/${id}`, data);
  return response.data;
};

/**
 * Delete RPC endpoint
 * @param id RPC endpoint ID
 * @returns Deletion result
 */
export const deleteRPC = async (id: string): Promise<{ success: boolean; message: string }> => {
  const response = await axios.delete(`${API_BASE}/rpc-monitor/delete/${id}`);
  return response.data;
};

/**
 * Delete multiple RPC endpoints
 * @param ids Array of RPC endpoint IDs
 * @returns Deletion result
 */
export const bulkDeleteRPCs = async (ids: string[]): Promise<{ success: boolean; message: string; deletedCount: number }> => {
  const response = await axios.post(`${API_BASE}/rpc-monitor/bulk-delete`, { ids });
  return response.data;
};

/**
 * Test multiple RPC endpoints
 * @param rpcIds Array of RPC endpoint IDs
 * @returns Test results
 */
export const bulkTestRPCs = async (rpcIds: string[]): Promise<{
  total: number;
  successCount: number;
  failureCount: number;
  results: Array<{
    _id: string;
    url: string;
    network: string;
    isHealthy: boolean;
    success: boolean;
    error?: string;
  }>;
  timestamp: Date;
}> => {
  const response = await axios.post(`${API_BASE}/rpc-monitor/test-bulk`, { rpcIds });
  return response.data;
};
